import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  TablePagination,
  Chip,
  Button,
  TableSortLabel,
} from '@mui/material'
import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { Sessao } from '../../types/api'
import dayjs from 'dayjs'

interface SessoesTableProps {
  sessoes: Sessao[]
  onRevogar: (sessaoId: number) => void
  onRevogarTodas: () => void
  loading?: boolean
}

const SessoesTable: React.FC<SessoesTableProps> = ({
  sessoes = [],
  onRevogar,
  onRevogarTodas,
  loading,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortField, setSortField] = useState<keyof Sessao | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null)

  const safeSessoes = Array.isArray(sessoes) ? sessoes : []

  const sortableColumns: Array<{ key: keyof Sessao; label: string; align?: 'left' | 'right' }> = [
    { key: 'id', label: t('common.id'), align: 'right' },
    { key: 'device_id', label: t('sessions.deviceId') },
    { key: 'device_name', label: t('sessions.deviceName') },
    { key: 'valido', label: t('common.status') },
    { key: 'access_token_expires_at', label: t('sessions.expiresAt') },
    { key: 'created_at', label: t('sessions.createdAt') },
  ]

  const sortValue = (sessao: Sessao, field: keyof Sessao) => {
    const value = sessao[field]
    if (value == null) return null

    if (typeof value === 'number') return value
    if (typeof value === 'boolean') return value ? 1 : 0

    if (field === 'access_token_expires_at' || field === 'created_at') {
      return dayjs(value as string).valueOf()
    }

    return String(value).toLowerCase()
  }

  const sortedSessoes = useMemo(() => {
    if (!sortField || !sortDirection) {
      return safeSessoes
    }

    const multiplier = sortDirection === 'asc' ? 1 : -1

    return [...safeSessoes].sort((a, b) => {
      const aValue = sortValue(a, sortField)
      const bValue = sortValue(b, sortField)

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return -1 * multiplier
      if (bValue == null) return 1 * multiplier

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * multiplier
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: 'base',
        numeric: true,
      }) * multiplier
    })
  }, [safeSessoes, sortDirection, sortField])

  // Paginação
  const paginatedSessoes = sortedSessoes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSort = (field: keyof Sessao) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
      setPage(0)
      return
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else if (sortDirection === 'desc') {
      setSortDirection(null)
      setSortField(null)
    } else {
      setSortDirection('asc')
    }
    setPage(0)
  }

  const getSortDirection = (field: keyof Sessao) => {
    if (sortField !== field || sortDirection == null) return 'asc'
    return sortDirection
  }

  const sessoesValidas = safeSessoes.filter((s) => s.valido).length

  if (loading) {
    return (
      <Box p={3}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    )
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">
          {t('sessions.activeSessionsCount', { valid: sessoesValidas, total: safeSessoes.length })}
        </Typography>
        {sessoesValidas > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<RefreshIcon />}
            onClick={onRevogarTodas}
            disabled={loading}
          >
            {t('sessions.revokeAll')}
          </Button>
        )}
      </Box>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortableColumns.map((column) => {
                const isActive = sortField === column.key && sortDirection !== null
                return (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    sortDirection={isActive ? sortDirection ?? 'asc' : false}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                )
              })}
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSessoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" py={3}>
                    {t('sessions.noSessions')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedSessoes.map((sessao) => (
                <TableRow key={sessao.id} hover>
                  <TableCell>{sessao.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {sessao.device_id ? sessao.device_id.substring(0, 8) + '...' : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {sessao.device_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sessao.valido ? t('sessions.valid') : t('sessions.revoked')}
                      color={sessao.valido ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {sessao.access_token_expires_at ? (
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(sessao.access_token_expires_at).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {sessao.created_at ? (
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(sessao.created_at).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {sessao.valido && (
                      <IconButton
                        size="small"
                        onClick={() => onRevogar(sessao.id)}
                        color="error"
                        aria-label={t('sessions.revoke')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedSessoes.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  )
}

export default SessoesTable

